import { useAuth0 } from "@auth0/auth0-react";
import { ReactComponent as FilterIcon } from "assets/icons/filter-icon.svg";
import CrossSvg from "assets/icons/svg/cross";
import { LabelField } from "components";
import InfoTooltip from "components/InfoTooltip";
import Button from "components/button/Button";
import MyButton from "components/MyButton";
import { getDateFormat, getDateFormatForDatePicket } from "helper/dateFormat";
import { useSearch } from "hook";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import Select from "react-select";
import { DateRangePicker } from "rsuite";
import FinanceService from "../../../Lib/API/Finance";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "#FFFFFF",
    borderColor: "#FFFFFF",
    color: "#b5b5c3",
    paddingTop: "6px",
    paddingBottom: "6px",
    cursor: "pointer",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "On-Hold", value: "on-hold" },
  { label: "Disabled", value: "disable" },
  { label: "Completed", value: "completed" },
];

const TYPE_OPTIONS = [
  { label: "Recurring", value: "recurring" },
  { label: "Instalment", value: "installment" },
  { label: "Future Payment", value: "futurePayment" },
];

const dateFormat = getDateFormatForDatePicket();
const placeholderFormat = getDateFormat();

const toDateRange = (dates) =>
  dates?.length === 2 && dates[0] && dates[1]
    ? { start: moment(dates[0]).format("MM/DD/YYYY"), end: moment(dates[1]).format("MM/DD/YYYY") }
    : undefined;

const PaymentPlansFilter = ({ filter, setFilter }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [openFilter, setOpenFilter] = useState(false);
  const [pendingStatus, setPendingStatus] = useState([]);
  const [pendingType, setPendingType] = useState([]);
  const [pendingAccount, setPendingAccount] = useState(null);
  const [createdDate, setCreatedDate] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [futurePaymentDate, setFuturePaymentDate] = useState(null);
  const [search, setSearch] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [onSearch, setOnSearch] = useState("");
  const { searchAction } = useSearch({ action: setOnSearch });

  useEffect(() => {
    async function fetchAccounts() {
      setAccountsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await FinanceService.getACcountNames(token, onSearch);
        if (res?.accounts) {
          setAccounts(res.accounts.map((item) => ({ ...item, label: item.family_name, value: item._id })));
        }
      } catch (err) {
        toastr.error("Error", err?.data?.message || "Failed to load accounts");
      } finally {
        setAccountsLoading(false);
      }
    }
    fetchAccounts();
  }, [onSearch, getAccessTokenSilently]);

  const onGo = () => {
    setFilter({
      ...(search && { search }),
      status: pendingStatus.map((s) => s.value),
      type: pendingType.map((t) => t.value),
      ...(pendingAccount && { accountId: pendingAccount._id }),
      ...(toDateRange(createdDate) && { createdAtRange: toDateRange(createdDate) }),
      ...(toDateRange(paymentDate) && { paymentDateRange: toDateRange(paymentDate) }),
      ...(toDateRange(futurePaymentDate) && { nextPaymentDateRange: toDateRange(futurePaymentDate) }),
    });
    setOpenFilter(false);
  };

  const onClear = () => {
    setPendingStatus([]);
    setPendingType([]);
    setPendingAccount(null);
    setCreatedDate(null);
    setPaymentDate(null);
    setFuturePaymentDate(null);
    setSearch("");
    setFilter({});
  };

  return (
    <div className="filteration">
      <div className="activated-ledger d-flex flex-column flex-lg-row justify-content-end mb-5">
        <div className="sub-header-functions d-flex align-items-center">
          <div className="pr-5">
            <Button
              style={openFilter ? { opacity: 0.3 } : {}}
              variant="filter"
              onClick={() => setOpenFilter((o) => !o)}
            >
              <FilterIcon />
              <span>Filter</span>
            </Button>
          </div>
        </div>
      </div>

      {openFilter && (
        <div className="filter mb-10">
          <div className="text-right px-5 pt-5">
            <span className="cursor-pointer" onClick={() => setOpenFilter(false)}>
              <CrossSvg />
            </span>
          </div>
          <div className="row pr-12 pl-9 finance-tilter">
            <div className="col-sm-3 col-md-3 charged_date">
              <LabelField title="Created Date" />
              <DateRangePicker
                format={dateFormat}
                placeholder={`${placeholderFormat}  ~  ${placeholderFormat}`}
                cleanable={true}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                style={{ display: "inline-block", padding: "0", border: "0", width: "100%", color: "black" }}
                size="lg"
                value={createdDate}
                onChange={(val) => setCreatedDate(val)}
              />
            </div>
            <div className="col-sm-3 col-md-3 charged_date">
              <LabelField title="Payment Date" />
              <DateRangePicker
                format={dateFormat}
                placeholder={`${placeholderFormat}  ~  ${placeholderFormat}`}
                cleanable={true}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                style={{ display: "inline-block", padding: "0", border: "0", width: "100%", color: "black" }}
                size="lg"
                value={paymentDate}
                onChange={(val) => setPaymentDate(val)}
              />
            </div>
            <div className="col-sm-3 col-md-3">
              <LabelField title="Account" />
              <Select
                classNamePrefix="react-select"
                options={accounts}
                styles={colourStyles}
                value={pendingAccount}
                onChange={(selected) => setPendingAccount(selected || null)}
                onInputChange={(text) => searchAction(text)}
                isLoading={accountsLoading}
                isClearable
                placeholder="Search account..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div className="col-sm-3 col-md-3">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LabelField title="Type" />
                <InfoTooltip text="Recurring bills on a repeating schedule, Instalment splits a charge into a fixed number of payments, Future Payment is scheduled to run once on a set date." style={{ marginLeft: '6px', marginBottom: '8px' }} />
              </div>
              <Select
                isMulti
                classNamePrefix="react-select"
                options={TYPE_OPTIONS}
                styles={colourStyles}
                value={pendingType}
                onChange={(selected) => setPendingType(selected || [])}
                placeholder="Select type..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
          <div className="row pr-12 pl-9 finance-tilter mt-4">
            <div className="col-sm-3 col-md-3 charged_date">
              <LabelField title="Next Payment" />
              <DateRangePicker
                format={dateFormat}
                placeholder={`${placeholderFormat}  ~  ${placeholderFormat}`}
                cleanable={true}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                style={{ display: "inline-block", padding: "0", border: "0", width: "100%", color: "black" }}
                size="lg"
                value={futurePaymentDate}
                onChange={(val) => setFuturePaymentDate(val)}
              />
            </div>
            <div className="col-sm-3 col-md-3">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LabelField title="Status" />
                <InfoTooltip text="Active plans are currently billing, On-Hold plans are paused, Completed plans have finished all payments, and Disabled plans were cancelled before completion." style={{ marginLeft: '6px', marginBottom: '8px' }} />
              </div>
              <Select
                isMulti
                classNamePrefix="react-select"
                options={STATUS_OPTIONS}
                styles={colourStyles}
                value={pendingStatus}
                onChange={(selected) => setPendingStatus(selected || [])}
                placeholder="Select status..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
          <div className="pr-12 pl-9 pb-10 justify-content-end d-flex">
            <MyButton
              type="secondary"
              content={<span className="mx-8">Clear</span>}
              onClick={onClear}
              style={{ marginRight: 10 }}
            />
            <button
              className="btn btn-primary"
              style={{ width: 70 }}
              onClick={onGo}
            >
              Go
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlansFilter;
